import { LAB_RANGES } from "features/cdm/constants/labRanges";

/**
 * Wraps a lab-result zod string schema with the spec-defined numeric range for
 * the given field (see LAB_RANGES). Fields without a defined range are returned
 * unchanged.
 *
 * The range is only enforced for actual numeric entries — empty values and
 * non-numeric sentinels such as "Not Available" / "NA" pass through, leaving
 * required/optional handling to the base schema.
 */
/**
 * True when `value` is a real numeric entry that falls outside the spec-defined
 * range for `fieldName`. Empty values and non-numeric sentinels ("Not Available"
 * / "NA") are never "out of range".
 *
 * Used to surface a non-blocking warning for fields whose out-of-range value is
 * recorded as 0 by the backend (HbA1c — HL7 spec §"Lab fields"), rather than
 * hard-blocking submission.
 */
export const isLabValueOutOfRange = (fieldName, value) => {
  const range = LAB_RANGES[fieldName];

  if (!range || value === undefined || value === null || value === "") {
    return false;
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return false;
  }

  return numericValue < range.min || numericValue > range.max;
};

export const withLabRange = (schema, fieldName, label) => {
  const range = LAB_RANGES[fieldName];

  if (!range) {
    return schema;
  }

  const { min, max, unit } = range;

  return schema.refine(
    (value) => {
      if (value === undefined || value === "") {
        return true;
      }

      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return true;
      }

      return numericValue >= min && numericValue <= max;
    },
    { message: `${label} must be between ${min} and ${max} ${unit}` }
  );
};
